import React, { useContext, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectKMatrixData } from '../../../../selectors/amr-pipeline.selector';
import { useKMatrixColumns } from './k-matrix-columns';
import { PipelineEmptyPlaceholder } from '../PipelineEmptyPlaceholder';
import { Table } from '../../../bidding/common/table';
import { SORT } from '../../../../constants';
import { SubscriptionFeature } from '../../../../types/billing/SubscriptionFeature';
import { user } from '../../../../user';
import PipelineContext from '../PipelineContext';
import { createAmrPipelineActions } from '../../../../actions/amr-pipeline.actions';
import { useAppDispatch } from '../../../../effects/useAppDispatch';
import { KMatrix } from '../../../../types/amr-pipeline/models/K-MatrixResponse';
import { BlockedFeatureContent, SubscribeLink } from '../../../access/BlockedFeatureText';
import { createSearchTransactionActions } from '../../../../actions';
import { EmptyPlaceholder } from '../../../common';
import { PipelineView } from '../../types/PipelineView';
import { IntersectionObserverProvider } from '../../../common/IntersectionObsereverProvider';

interface KMatrixViewProps {
    onRedirect?: () => void;
}

export const KMatrixView: React.FC<KMatrixViewProps> = ({ onRedirect = () => { } }) => {
    const dispatch = useAppDispatch();
    const { pipelineType } = useContext(PipelineContext);

    const { kMatrix, isKMatrixSearching, kMatrixViewType, kMatrixSortBy, kMatrixSortDirection, numberOfHiddenKMatrix, hasMoreKMatrix } =
        useSelector(selectKMatrixData);

    const columns = useKMatrixColumns(kMatrixViewType);

    const withImSubscription = user.hasFeatures(SubscriptionFeature.IssuanceMonitorFullAccess);
    const actions = useMemo(() => createAmrPipelineActions(pipelineType), [pipelineType]);
    const searchTransactionsActions = useMemo(() => createSearchTransactionActions(pipelineType), [pipelineType]);

    const handleNextPageRequest = useCallback(() => {
        if (!isKMatrixSearching) {
            dispatch(actions.loadKMatrix());
        }
    }, [isKMatrixSearching, dispatch, actions]);

    const onSort = useCallback(
        (field: string) => {
            const switchSorting = (sort: string) => (sort === SORT.ASC ? SORT.DESC : SORT.ASC);
            dispatch(actions.kMatrixSortingChanged(field, switchSorting(kMatrixSortDirection ?? SORT.ASC)));
            dispatch(actions.applyFilterAndSearch(PipelineView.KMatrix));
        },
        [dispatch, actions, kMatrixSortDirection],
    );

    const sortBy = kMatrixSortBy;
    const sortDirection = kMatrixSortDirection;

    const nonVisibleCount = numberOfHiddenKMatrix;
    const hasMoreItems = hasMoreKMatrix;
    const hasInvisibleDeals = !hasMoreItems && !!(nonVisibleCount && nonVisibleCount > 0);

    if (!kMatrix) {
        return <PipelineEmptyPlaceholder onRedirect={onRedirect} />;
    }

    const handleResetFilter = () => dispatch(actions.filterReset());

    const handleResetFilterAndSearch = () => {
        dispatch(searchTransactionsActions.reset());
        handleResetFilter();
    };

    const renderLastRow = () => {
        if (!hasInvisibleDeals || withImSubscription) {
            return null;
        }

        const description = `to see ${nonVisibleCount} more deal(s).`;

        return (
            <BlockedFeatureContent
                inline
                className="invisible-data-placeholder"
                text={
                    <>
                        <SubscribeLink /> {description}
                    </>
                }
            />
        );
    };

    const renderEmptyPlaceholder = () => <EmptyPlaceholder onResetClick={handleResetFilterAndSearch} />;


    return !kMatrix?.length && !isKMatrixSearching ? renderEmptyPlaceholder() : (
        <IntersectionObserverProvider>
            <Table
                tableKey={PipelineView.KMatrix}
                infiniteScrollEnabled={hasMoreItems}
                isNextPageRequesting={hasMoreItems}
                defaultSortBy={sortBy}
                defaultSortByDirection={sortDirection}
                sticky
                className={`pipeline-list-k-matrix data-list-header-two-rows`}
                dataItems={kMatrix}
                createRowCustomKey={(item: KMatrix) =>
                    `${item.transactionReferenceName}-${item.dealReferenceName}-${item.lastUpdated}`
                }
                columns={columns}
                collapsible={false}
                renderLastRow={renderLastRow}
                onSort={onSort}
                onNextPageRequest={handleNextPageRequest}
            />
        </IntersectionObserverProvider>
    );
};
